import {
  Component, Input, OnChanges, SimpleChanges, inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DateTimePipe } from '../../pipes';
import {
  DeviceHistoryItem,
  DeviceHistoryOrganizationValue,
  DeviceStatus,
} from '../../models';
import { OrganizationService } from '../../services';

@Component({
  selector: 'app-device-history-list',
  standalone: true,
  imports: [CommonModule, TranslatePipe, DateTimePipe],
  templateUrl: './device-history-list.component.html',
})
export class DeviceHistoryListComponent implements OnChanges {
  private organizationService = inject(OrganizationService);
  private translate = inject(TranslateService);

  @Input() historyItems: DeviceHistoryItem[] = [];

  protected organizationNameMap: Record<string, string> = {};

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['historyItems']) {
      this.loadOrganizationNames();
    }
  }

  protected hasStatusChange(item: DeviceHistoryItem): boolean {
    return !!item.oldStatus || !!item.newStatus;
  }

  protected hasAssignedOrganizationChange(item: DeviceHistoryItem): boolean {
    return !!item.oldAssignedOrganization || !!item.newAssignedOrganization;
  }

  protected hasUsingOrganizationChange(item: DeviceHistoryItem): boolean {
    return !!item.oldUsingOrganization || !!item.newUsingOrganization;
  }

  protected hasSeenOrganizationChange(item: DeviceHistoryItem): boolean {
    return !!item.oldSeenOrganization || !!item.newSeenOrganization;
  }

  protected organizationLabel(value?: DeviceHistoryOrganizationValue | null): string {
    if (!value) {
      return '-';
    }
    if (value.id) {
      return this.organizationNameMap[value.id] ?? value.id;
    }
    if (value.hidden) {
      return this.translate.instant('deviceHistory.hiddenOrganization');
    }
    return '-';
  }

  protected statusLabel(status?: DeviceStatus | null): string {
    if (!status) {
      return '-';
    }
    return this.translate.instant(`devices.statusValues.${status}`);
  }

  protected statusTransitionLabel(item: DeviceHistoryItem): string {
    return `${this.statusLabel(item.oldStatus)} -> ${this.statusLabel(item.newStatus)}`;
  }

  private loadOrganizationNames(): void {
    const organizationIds = this.historyItems
      .flatMap((item) => [
        item.oldAssignedOrganization?.id,
        item.newAssignedOrganization?.id,
        item.oldUsingOrganization?.id,
        item.newUsingOrganization?.id,
        item.oldSeenOrganization?.id,
        item.newSeenOrganization?.id,
      ])
      .filter((value): value is string => !!value);

    if (organizationIds.length === 0) {
      this.organizationNameMap = {};
      return;
    }

    this.organizationService.getNameMap(organizationIds).subscribe((result) => {
      this.organizationNameMap = result;
    });
  }
}
